import { createContext, FC, useEffect, useMemo, useState } from 'react'
import { DragDropContext, DraggableLocation, DragStart, DropResult } from 'react-beautiful-dnd'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import isNumber from 'lodash/isNumber'
import { Box, Divider } from '@mui/material'

import { DraggableFormElement } from '@pages/FormCreateOrEdit/types'

import { useUpdateWindowMutation } from '@redux/api/windows.api'

import { useAppDispatch } from '@hooks'
import {
  add,
  getElementPathByDroppableId,
  getLineById,
  getLinePathByDroppableId,
  move,
  reorder,
  showSnackbarNotification,
} from '@helpers'
import {
  AVAILABLE_FORM_ELEMENT_TYPE,
  FORM_DROPPABLE_TYPE,
  FORM_FIELD_LABEL_POSITION,
  GRID_SIZE,
} from '@constants'
import {
  GETWindowDTO,
  WindowConfigContainerType,
  WindowConfigElement,
  WindowConfigLine,
} from '@types'

import { FormSectionType } from '../../types/types'

import { useWindowConfig } from './hooks/useWindowConfig'
import { AvailableSectionElements } from './AvailableSectionElements'
import { ConfiguredSectionsElements } from './ConfiguredSectionsElements'

type WindowConfigContextType = {
  availableSections?: DraggableFormElement[]
  isLoadingWindowById?: boolean
  containers?: WindowConfigContainerType[]
  currentDragged?: DragStart | undefined
  windowById?: GETWindowDTO | undefined
  onTransformConfigsToPOST: (
    containers: WindowConfigContainerType[]
  ) => WindowConfigLine<string | number>[]
}

export const WindowConfigContext = createContext<WindowConfigContextType>(
  {} as WindowConfigContextType
)

type WindowConfigProps = unknown

export const WindowConfig: FC<WindowConfigProps> = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [currentDragged, setCurrentDragged] = useState<DragStart>()
  const [unallocatedSections, setUnallocatedSections] = useState<FormSectionType[]>([])
  const [updateWindow, { isLoading: isLoadingUpdateWindow }] = useUpdateWindowMutation()

  const { handlers, data, state } = useWindowConfig()

  const { unusedSections, isLoadingWindowById, windowId, windowById, configs, initialContainers } =
    data
  const { handleTransformConfigsToPOST, transformConfigsToContainers } = handlers

  const showFullRowMessage = () => {
    showSnackbarNotification({
      text: t('notifications.rowIsFull'),
      options: {
        variant: 'info',
      },
    })
  }

  useEffect(() => {
    setUnallocatedSections(unallocatedSections || [])
  }, [unallocatedSections])

  const [containers, setContainers] = useState<WindowConfigContainerType[]>(initialContainers)

  useEffect(() => {
    if (configs) {
      setContainers(transformConfigsToContainers(configs, initialContainers))
    }
  }, [configs])

  const availableSections = useMemo(
    (): DraggableFormElement[] =>
      unusedSections.map(section => ({
        id: section.id,
        type: AVAILABLE_FORM_ELEMENT_TYPE.SECTION,
        title: section.title ? section.title : '',
        labelPosition: FORM_FIELD_LABEL_POSITION.TOP,
        titleHidden: false,
      })),
    [unusedSections]
  )

  const addNewElement = (source: DraggableLocation, destination: DraggableLocation) => {
    const [containerId, tabId, lineId] = getElementPathByDroppableId(destination.droppableId)
    const newContainers = structuredClone(containers) as WindowConfigContainerType[]
    const { line } = getLineById(newContainers, containerId, tabId, lineId)

    if (!line) {
      return
    }

    let newElement = {} as Omit<WindowConfigElement, 'id'>

    const rowSize = (line as WindowConfigLine<number | string>).elements.reduce(
      (a, b) => a + b.size,
      0
    )

    const elementSize = rowSize === 11 ? 1 : 2
    if (rowSize === 12) {
      showFullRowMessage()

      return
    }

    if (source.droppableId.includes(AVAILABLE_FORM_ELEMENT_TYPE.SECTION)) {
      const draggedField = availableSections[source.index]

      const newSections = [...unallocatedSections].filter(({ id }) => id !== draggedField.id)
      // Принудительно вызываем ререндер списка, иначе dragging item мерцает, возвращаясь в исходный список
      flushSync(() => {
        setUnallocatedSections(newSections)
      })

      newElement = {
        order: destination.index,
        size: 6,
        // type: FORM_ELEMENT_TYPE.FIELD,
        // pointId: Number(draggedField.id),
        lineId: 0,
        title: draggedField.title,
      }
    }

    line.elements = add(
      {
        id: currentDragged?.draggableId,
        ...newElement,
      },
      line.elements,
      destination
    )[destination.droppableId]

    setContainers(newContainers)

    const configs = handleTransformConfigsToPOST(newContainers)

    updateWindow({
      ...windowById,
      configs,
    })
  }

  const reorderElements = (source: DraggableLocation, destination: DraggableLocation) => {
    const [containerId, tabId, lineId] = getElementPathByDroppableId(source.droppableId)
    const newContainers = structuredClone(containers) as WindowConfigContainerType[]
    const { line } = getLineById(newContainers, containerId, tabId, lineId)

    if (!line) {
      return
    }

    const newElements = reorder(line.elements, source.index, destination.index).map(
      (element, index) => ({
        ...element,
        order: index,
      })
    )
    line.elements = newElements
    setContainers(newContainers)

    const configs = handleTransformConfigsToPOST(newContainers)

    updateWindow({
      ...windowById,
      configs,
    })
  }

  const handleDragStart = (initial: DragStart) => {
    setCurrentDragged(initial)
  }

  const moveElements = (source: DraggableLocation, destination: DraggableLocation) => {
    const [sContainerId, sTabId, sLineId] = getElementPathByDroppableId(source.droppableId)
    const [dContainerId, dTabId, dLineId] = getElementPathByDroppableId(destination.droppableId)

    const newContainers = structuredClone(containers) as WindowConfigContainerType[]

    if (isNumber(sContainerId) && isNumber(dContainerId)) {
      const { line: sLine } = getLineById(newContainers, sContainerId, sTabId, sLineId)
      const { line: dLine } = getLineById(newContainers, dContainerId, dTabId, dLineId)

      const countSizeRow = dLine?.elements.reduce((a, b) => a + b.size, 0)

      if (countSizeRow === 12) {
        showFullRowMessage()

        return
      }

      // Если перетаскиваемый элемент больше чем свободное место в строке
      if (sLine && countSizeRow && sLine?.elements[source.index].size > GRID_SIZE - countSizeRow) {
        showFullRowMessage()

        return
      }

      if (!sLine || !dLine) {
        return
      }

      const moveResult = move(sLine.elements, dLine.elements, source, destination)
      sLine.elements = moveResult[source.droppableId].map((element, index) => ({
        ...element,
        order: index,
      }))
      dLine.elements = moveResult[destination.droppableId].map((element, index) => ({
        ...element,
        order: index,
      }))
      setContainers(newContainers)

      const { id, title, order, size } = dLine.elements[destination.index]

      const configs = handleTransformConfigsToPOST(newContainers)

      updateWindow({
        ...windowById,
        configs,
      })
    }
  }

  const reorderLines = (source: DraggableLocation, destination: DraggableLocation) => {
    const [sContainerId, sTabId] = getLinePathByDroppableId(source.droppableId)
    const [dContainerId, dTabId] = getLinePathByDroppableId(destination.droppableId)

    const newContainers = structuredClone(containers) as WindowConfigContainerType[]
    const sContainer = newContainers.find(item => item.id === sContainerId)
    const dContainer = newContainers.find(item => item.id === dContainerId)

    if (!sContainer || !dContainer) {
      return
    }

    const sTab = sContainer.tabs.find(item => item.id === sTabId)
    const dTab = dContainer.tabs.find(item => item.id === dTabId)

    if (!sTab || !dTab) {
      return
    }

    if (sTab === dTab) {
      dTab.lines = reorder(dTab.lines, source.index, destination.index).map((line, index) => ({
        ...line,
        order: index,
      }))
    } else {
      const moveResult = move(sTab.lines, dTab.lines, source, destination)
      sTab.lines = moveResult[source.droppableId].map((line, index) => ({ ...line, order: index }))
      dTab.lines = moveResult[destination.droppableId].map((line, index) => ({
        ...line,
        order: index,
      }))
    }
    setContainers(newContainers)

    const { elements, ...body } = dTab.lines[destination.index]
    const configs = handleTransformConfigsToPOST(newContainers)

    updateWindow({
      ...windowById,
      configs,
    })
  }

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result

    setCurrentDragged(undefined)

    if (!destination) {
      return
    }

    if (destination.index === source.index && destination.droppableId === source.droppableId) {
      return
    }

    const sInd = source.droppableId
    const dInd = destination.droppableId

    if (result.type === FORM_DROPPABLE_TYPE.LINES) {
      reorderLines(source, destination)

      return
    }

    if (result.type === FORM_DROPPABLE_TYPE.DEFAULT) {
      if (sInd.match(/(SECTION):/g)) {
        addNewElement(source, destination)

        return
      }
      if (sInd === dInd) {
        reorderElements(source, destination)
      } else {
        moveElements(source, destination)
      }

      return
    }
  }

  const contextValue = {
    availableSections,
    isLoadingWindowById,
    containers,
    currentDragged,
    windowById,
    onTransformConfigsToPOST: handleTransformConfigsToPOST,
  }

  return (
    <Box sx={{ display: 'flex', paddingTop: '16px' }}>
      <WindowConfigContext.Provider value={contextValue}>
        <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
          <AvailableSectionElements />
          <Divider flexItem orientation={'vertical'} />
          <ConfiguredSectionsElements />
        </DragDropContext>
      </WindowConfigContext.Provider>
    </Box>
  )
}
